import { AutoComplete, Input, Select, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useRef } from 'react';
import {
	OPERATORS,
	valueKind,
} from 'src/_shared/services/job-sync/operators';
import {
	setConditionCaseInsensitive,
	setConditionField,
	setConditionOperator,
	setConditionValue,
} from 'src/_shared/services/job-sync/serde';

// caseInsensitive is meaningless for the numeric comparators and the no-value operators.
const NO_CI = new Set(['gt', 'lt', 'gte', 'lte', 'is_null', 'is_not_null']);

// One `Condition` = `field operator value?`. Fully controlled: `condition` in, `onChange(next)` out.
// The value control is chosen from the operator's arity (none / array / scalar) so the emitted JSON can
// never disagree with the operator; switching operators recalls the last scalar/array the user typed.
export default function ConditionEditor({
	condition,
	fieldOptions = [],
	onChange,
	onRemove,
	canRemove,
}) {
	const recall = useRef({ scalar: undefined, array: undefined });
	const kind = valueKind(condition.operator);
	const supportsCI = !NO_CI.has(condition.operator);

	const handleOperator = (op) => {
		// stash the current value so switching back restores it
		if (kind === 'scalar') recall.current.scalar = condition.value;
		if (kind === 'array') recall.current.array = condition.value;

		let next = setConditionOperator(condition, op);
		const nextKind = valueKind(op);
		if (
			nextKind === 'scalar' &&
			(next.value === '' || next.value === undefined) &&
			recall.current.scalar !== undefined &&
			recall.current.scalar !== ''
		) {
			next = setConditionValue(next, recall.current.scalar);
		}
		if (
			nextKind === 'array' &&
			Array.isArray(next.value) &&
			next.value.length === 0 &&
			Array.isArray(recall.current.array) &&
			recall.current.array.length > 0
		) {
			next = setConditionValue(next, recall.current.array);
		}
		onChange(next);
	};

	return (
		<div className="js-cond">
			<div className="js-token js-token-field">
				<AutoComplete
					value={condition.field}
					options={fieldOptions.map((f) => ({ value: f }))}
					filterOption={(input, option) =>
						String(option.value).toLowerCase().includes(input.toLowerCase())
					}
					onChange={(field) => onChange(setConditionField(condition, field))}
					placeholder="field"
					style={{ width: '100%' }}
				/>
			</div>

			<div className="js-token js-token-op">
				<Select
					value={condition.operator}
					onChange={handleOperator}
					options={OPERATORS.map((o) => ({ value: o.value, label: o.label }))}
					style={{ width: '100%' }}
					dropdownMatchSelectWidth={false}
				/>
			</div>

			{kind !== 'none' && (
				<div className={`js-token js-token-val${kind === 'array' ? ' js-token-tags' : ''}`}>
					{kind === 'array' ? (
						<Select
							mode="tags"
							value={Array.isArray(condition.value) ? condition.value : []}
							onChange={(vals) => onChange(setConditionValue(condition, vals))}
							tokenSeparators={[',']}
							placeholder="values…"
							style={{ width: '100%' }}
							open={false}
						/>
					) : (
						<Input
							value={condition.value == null ? '' : String(condition.value)}
							onChange={(e) => onChange(setConditionValue(condition, e.target.value))}
							placeholder="value"
						/>
					)}
				</div>
			)}

			{supportsCI && (
				<Tooltip title={condition.caseInsensitive ? 'Case-insensitive' : 'Case-sensitive'}>
					<button
						type="button"
						className={`js-iconbtn${condition.caseInsensitive ? ' is-on' : ''}`}
						onClick={() =>
							onChange(setConditionCaseInsensitive(condition, !condition.caseInsensitive))
						}
						aria-pressed={!!condition.caseInsensitive}
					>
						<span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>Aa</span>
					</button>
				</Tooltip>
			)}

			{canRemove && (
				<Tooltip title="Remove condition">
					<button type="button" className="js-iconbtn js-danger" onClick={onRemove} aria-label="Remove condition">
						<CloseOutlined />
					</button>
				</Tooltip>
			)}
		</div>
	);
}
